import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Form,
  FormItem,
  RadioButton,
  Typography,
  Checkbox,
} from '@frontend/design_system'
import {
  ADDED_SUCCESS,
  CROSS_BORDER_POLICY,
  CROSS_BORDER_POLICY_DESCRIPTION,
  CROSS_BORDER_POLICY_FORM_VALUES,
  UPDATED_SUCCESS,
} from 'texts/termsAndConditions'
import { IFormValues } from './types'
import { Block } from '../Block'
import { FormChildren, FormItemChildren, SelectValue } from 'types/form'
import { ActionButtons } from '../ActionButtons'
import { Row } from '../Row'
import { RADIO_BUTTONS } from './constants'
import { Countries } from './components/Countries'
import { useCountriesRules } from './hooks/useCountriesRules'
import { IContentProps } from 'modules/TermsAndConditions/types'
import { useApiRequest } from 'hooks/useApiRequest'
import { referencesApi, termsApi } from 'api'
import {
  CROSS_BORDER_POLICY_TYPES,
  TERM_CONDITIONS_TYPES,
} from 'constants/termsAndConditions'
import { useDispatch } from 'react-redux'
import { setNotificationMessage } from 'redux/notifications/slice'
import { prepareSelectDataByIds } from 'utils/form'
import { useCompanyId } from 'hooks/useCompanyId'
import { SAVE } from 'texts/uiTexts'
import { ADD } from 'texts/common'
import { UnsavedChangesModal } from 'ui/components/UnsavedChangesModal'
import { USA_ID } from 'constants/locations'
import { checkStateValues, ErrorState } from './utils'
import styles from './styles.module.scss'

export const CrossBorderPolicy = ({ reload, data }: IContentProps) => {
  const dispatch = useDispatch()
  const companyId = useCompanyId()

  const [countries, setCountries] = useState<SelectValue[]>([])
  const [states, setStates] = useState<SelectValue[]>([])
  const [errors, setErrors] = useState<ErrorState>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const newTermConditionRequest = useApiRequest(
    (body) => termsApi.newTermCondition(body),
    undefined,
    true,
    undefined,
    false
  )
  const updateTermConditionRequest = useApiRequest(
    (body) => termsApi.updateTermCondition(body),
    undefined,
    true,
    undefined,
    false
  )
  const countriesRules = useCountriesRules()

  const crossBorderPolicyItemRef = useRef<HTMLDivElement | null>(null)

  const countriesRequest = useApiRequest(
    () => referencesApi.getCountries(String(companyId), true, true),
    undefined,
    true,
    undefined,
    false
  )

  useEffect(() => {
    const fetchCountries = async () => {
      const countriesResponse = await countriesRequest.apiRequest()
      if (countriesResponse) {
        setCountries(
          countriesResponse.data.items.map((el) => ({
            label: el.name || '',
            value: el.id,
          }))
        )
        setStates(
          countriesResponse.data.items
            .find((el) => el.id === String(USA_ID))
            ?.meta?.States?.map((el) => ({
              label: el.name,
              value: el.id || '',
            })) || []
        )
      }
    }
    fetchCountries()
  }, [])

  const initValues = useMemo(() => {
    const initData: IFormValues = {
      crossBorderPolicy: CROSS_BORDER_POLICY_FORM_VALUES.ALLOWED_TO.ID,
      isIslandRestricted: true,
      countryIds: [
        {
          id: Date.now(),
          countryList: countries,
          price: '',
          usaStateIds: [],
          state: '',
          province: '',
        },
      ],
    }
    if (data) {
      initData.crossBorderPolicy =
        data.crossBorderPolicy || initData.crossBorderPolicy
      initData.isIslandRestricted =
        typeof data.isIslandRestricted === 'boolean'
          ? data.isIslandRestricted
          : initData.isIslandRestricted
      initData.countryIds =
        data.details?.map(
          (
            { countryIds, crossBorderFee, usaStateIds, state, province },
            id
          ) => ({
            id,
            usaStateIds: prepareSelectDataByIds(usaStateIds || [], states),
            state,
            province,
            countryList: prepareSelectDataByIds(countryIds || [], countries),
            price: String(
              typeof crossBorderFee === 'number' ? crossBorderFee : ''
            ),
          })
        ) || initData.countryIds
    }
    return initData
  }, [data, countries])

  const onSubmit = async (values: IFormValues, validate: boolean) => {
    const isNotAllowedTo =
      values.crossBorderPolicy !== CROSS_BORDER_POLICY_TYPES.AllowedTo
    let isValid = true
    if (!isNotAllowedTo) {
      isValid = checkStateValues(values, setErrors)
    }

    if (!isValid) {
      requestAnimationFrame(() => {
        const field = crossBorderPolicyItemRef.current?.querySelector(
          '[data-form-error="error"]'
        )

        if (field) {
          field.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      })
    }

    setIsSubmitting(false)
    if (validate && isValid) {
      setIsSubmitting(true)
      const details = values.countryIds.map(
        ({ countryList, price, usaStateIds, state, province }) => ({
          countryIds: countryList.map(({ value }) => Number(value)),
          crossBorderFee: +price,
          usaStateIds: usaStateIds?.map(({ value }) => Number(value)),
          state: state,
          province: province,
        })
      )
      const body = {
        termConditionTypeId: TERM_CONDITIONS_TYPES.CrossBorderPolicy,
        companyId,
        crossBorderPolicy: values.crossBorderPolicy,
        isIslandRestricted: values.isIslandRestricted,
        details: !isNotAllowedTo
          ? details
          : [{ countries: [], crossBorderFee: 0 }],
      }

      if (data) {
        await updateTermConditionRequest.apiRequest({
          ...body,
          termConditionSettingId: data.id,
        })
        dispatch(
          setNotificationMessage({
            notificationMessage: UPDATED_SUCCESS,
          })
        )
      } else {
        await newTermConditionRequest.apiRequest(body)
        dispatch(
          setNotificationMessage({
            notificationMessage: ADDED_SUCCESS,
          })
        )
      }

      reload()
    }
  }

  return !countriesRequest.loading ? (
    <Block
      title={CROSS_BORDER_POLICY}
      description={CROSS_BORDER_POLICY_DESCRIPTION}
    >
      <Form initValues={initValues} onSubmit={onSubmit} key={data?.id}>
        {({ isDirty, values }: FormChildren) => {
          const isAllowedTo =
            values[CROSS_BORDER_POLICY_FORM_VALUES.ALLOWED.ID] ===
            CROSS_BORDER_POLICY_FORM_VALUES.ALLOWED_TO.ID
          return (
            <>
              <UnsavedChangesModal isDirty={isDirty && !isSubmitting} />
              <FormItem
                id={CROSS_BORDER_POLICY_FORM_VALUES.ALLOWED.ID}
                className={styles.allowed}
              >
                {({ value, onChange }: FormItemChildren) => (
                  <Row>
                    {RADIO_BUTTONS.map(({ label, id }) => (
                      <RadioButton
                        labelTypographyName="body2WMedium"
                        key={id}
                        id={id}
                        label={label}
                        onChange={onChange}
                        checked={id === value}
                      />
                    ))}
                  </Row>
                )}
              </FormItem>
              {isAllowedTo && (
                <>
                  <FormItem
                    id={CROSS_BORDER_POLICY_FORM_VALUES.COUNTRIES.ID}
                    rules={countriesRules}
                    className={styles.countries}
                    isCleanUpOnUnmount={false}
                  >
                    {({ value, onChange, error, onBlur }: FormItemChildren) => (
                      <Countries
                        ref={crossBorderPolicyItemRef}
                        values={value}
                        onChange={onChange}
                        error={error}
                        errors={errors}
                        onBlur={onBlur}
                        countries={countries}
                        onChangeError={setErrors}
                        states={states}
                      />
                    )}
                  </FormItem>
                </>
              )}
              <div className={styles['island-restrictions']}>
                <Typography name="body2WMedium">
                  {CROSS_BORDER_POLICY_FORM_VALUES.ISLAND_RESTRICTIONS.TITLE}
                </Typography>
                <FormItem
                  id={CROSS_BORDER_POLICY_FORM_VALUES.ISLAND_RESTRICTIONS.ID}
                >
                  {({ value, onChange }: FormItemChildren) => (
                    <Checkbox
                      label={
                        CROSS_BORDER_POLICY_FORM_VALUES.ISLAND_RESTRICTIONS
                          .LABEL
                      }
                      onChange={onChange}
                      checked={value}
                      variant="tertiary"
                      borderVariant="grey"
                      size="newFontsSecondary"
                      verticalAlign="center"
                    />
                  )}
                </FormItem>
              </div>
              <ActionButtons
                isOffset={!isAllowedTo}
                disabled={!isDirty}
                submitLabel={data ? SAVE : ADD}
                loading={
                  newTermConditionRequest.loading ||
                  updateTermConditionRequest.loading
                }
              />
            </>
          )
        }}
      </Form>
    </Block>
  ) : null
}
